/**
 * Safe navigation for realtime targets — warm and cold start
 * (phase6-contract.md §3.7, master plan TASK-515).
 *
 * ONE router entry for every "open what this notification points at" —
 * {@link navigateToRealtimeTarget} — and ONE parking lot for the targets that
 * cannot be opened yet: a device-alert tap or a deep link that lands before
 * sign-in, or the route the guard bounced off to sign-in. The parked action
 * lives in `sessionStorage['syntry.pending_action.v1']` (this tab only) with
 * a TTL, stamped with the tenant + user it was issued for, and is replayed by
 * exactly ONE consumer per path: `SignInPage` on login (both success sites)
 * and `App.vue` on the `restore()` path — both call {@link consumePendingRoute}.
 *
 * Why the stamps: a notification is bound to the tenant + recipient it was
 * emitted for. Replaying a parked target after someone ELSE signs in on the
 * same device — a different company, or a colleague — would open a thread
 * page for a foreign tenant, so the consumer drops any action whose tenant or
 * user differ from the session that is now authenticated, and a live tap for
 * a foreign tenant is refused with a toast instead of parked. The stamp is
 * `tenantStamp()` / `normalizeTenant()` from `services/session-scope.ts` — THE
 * normaliser; nothing here compares raw company keys.
 *
 * Sources (the call sites, each a one-line substitution): the realtime toast
 * action (`realtime.service.ts`), `openTarget()` in the device facade (web
 * click / native tap — the tap listener is attached at boot, so a cold-start
 * tap is parked here), the push action tap (`push.service.ts`), the worker's
 * `syntry:notification-click` message (via `register.ts` `onWorkerMessage`),
 * the router guard ({@link parkRouteForSignIn}) and the deep link parsed in
 * `main.ts` before `createApp` ({@link captureDeepLink}).
 *
 * `recordOpened()` (realtime.service.ts §3.1) is called on every successful
 * navigation that carries an event id; the service parks the receipt until
 * receipts are enabled, so a cold-start open is never lost. It is resolved
 * lazily (the same shape `push.service.ts` uses for `reconcile()`): the
 * realtime service imports THIS module for the toast action, so a static
 * import back would close the cycle at module-evaluation time.
 */
import type { RouteLocationRaw } from 'vue-router';
import router from '@/router';
import { PENDING_ACTION_TTL_MS } from '@/contracts/realtime-policy';
import { useToast } from '@/composables/useToast';
import { realtimeNavigationCopy } from '@/i18n/realtime-navigation.en';
import { hasAccessToken } from '@/services/api/erpat-client';
import { getSessionScope, normalizeTenant, tenantStamp } from '@/services/session-scope';

/** `sessionStorage` key of the parked action — this tab only, one at a time. */
export const PENDING_ACTION_STORAGE_KEY = 'syntry.pending_action.v1';

/** The inbox path — where an unresolvable target lands. */
const INBOX_PATH = '/tabs/inbox';

/**
 * The cold-start fragment the worker appends when it opens a NEW window for a
 * notification (`openWindow(url + '#erpat-rt=' + eventId)`). Same shape the
 * ERPat web client parses; matched case-insensitively and lower-cased.
 */
const COLD_START_RE = /^#erpat-rt=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The inbox deep link's `?id=` — a decimal notification id. */
const DIGITS_RE = /^[0-9]{1,20}$/;

/** Who a target was issued for. `userId: ''` = unknown (a guard park, a URL deep link). */
export interface RealtimeTargetContext {
  /** The tenant stamp the target belongs to (compared through `normalizeTenant()`). */
  tenant: string;
  /** The recipient's user id as a decimal string, or '' when the source cannot know it. */
  userId: string;
  /** The envelope's `event_id` — for the `opened` receipt — or `null`. */
  eventId: string | null;
}

/** What is parked while the target cannot be opened. */
export interface PendingAction extends RealtimeTargetContext {
  /** The router `fullPath` to replay (`/tabs/inbox?id=12`, `/tabs/messages/group/7`, …). */
  target: string;
  /** Epoch ms when it was parked — dropped once older than `PENDING_ACTION_TTL_MS`. */
  at: number;
}

export type RealtimeNavigationResult =
  /** Navigation was issued for the current session. */
  | 'navigated'
  /** Not authenticated (or issued for another user of the same tenant): parked for the consumer. */
  | 'parked'
  /** Authenticated, but the target belongs to another company: refused with a toast. */
  | 'foreign_tenant';

/* ── Storage ────────────────────────────────────────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The parked action as stored — shape-checked, not yet judged (TTL / stamps). */
export function readPendingAction(): PendingAction | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ACTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    if (typeof parsed.target !== 'string' || parsed.target === '') return null;
    if (typeof parsed.at !== 'number' || !Number.isFinite(parsed.at)) return null;
    return {
      target: parsed.target,
      tenant: normalizeTenant(typeof parsed.tenant === 'string' ? parsed.tenant : ''),
      userId: typeof parsed.userId === 'string' ? parsed.userId : '',
      eventId: typeof parsed.eventId === 'string' && parsed.eventId !== '' ? parsed.eventId : null,
      at: parsed.at,
    };
  } catch {
    return null;
  }
}

function writePendingAction(action: PendingAction): void {
  try {
    sessionStorage.setItem(PENDING_ACTION_STORAGE_KEY, JSON.stringify(action));
  } catch {
    /* storage unavailable — the target is lost, never thrown */
  }
}

/** Forget the parked action. Runs in `signOutLocal()` (a company switch is a sign-out). */
export function clearPendingAction(): void {
  try {
    sessionStorage.removeItem(PENDING_ACTION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function isExpired(action: PendingAction, now: number): boolean {
  return now - action.at > PENDING_ACTION_TTL_MS;
}

/* ── Stamps ─────────────────────────────────────────────────────────────────── */

/**
 * `true` when the action's stamps match the session that is authenticated NOW.
 *
 * An EMPTY tenant stamp is "no session to be foreign to" (DEC-099): a deep link
 * or a guard bounce on a fresh install parks before any company key is stored
 * (`rememberCompanyKey()` runs on the first login), and a native push tap
 * carries no tenant until Phase 7 adds `tenant_scope` (DEC-094). Refusing
 * those would drop every first-run deep link; a PRESENT stamp is still
 * compared strictly, and the server validates any `opened` receipt it gets.
 */
function stampsMatchSession(context: RealtimeTargetContext): boolean {
  const tenant = normalizeTenant(context.tenant);
  if (tenant !== '' && tenant !== tenantStamp()) return false;
  return context.userId === '' || context.userId === getSessionScope().userId;
}

/** A router location as the string the parking lot stores. */
function toFullPath(target: RouteLocationRaw): string {
  if (typeof target === 'string') return target === '' ? INBOX_PATH : target;
  try {
    return router.resolve(target).fullPath;
  } catch {
    return INBOX_PATH;
  }
}

/**
 * `recordOpened(eventId)` from `realtime.service.ts` (§3.1), resolved lazily —
 * see the module comment. The service's bounded queue holds the receipt until
 * the bootstrap says receipts are enabled. A missing export (an older build of
 * the service) or a throw is swallowed: a receipt must never break navigation.
 */
function reportOpened(eventId: string | null): void {
  if (!eventId) return;
  void import('@/services/realtime.service')
    .then((mod: unknown) => {
      const fn = mod && typeof mod === 'object' ? (mod as Record<string, unknown>).recordOpened : undefined;
      if (typeof fn === 'function') (fn as (id: string) => void)(eventId);
    })
    .catch(() => undefined);
}

/* ── The one router entry ───────────────────────────────────────────────────── */

/**
 * Open a realtime target — or park it.
 *
 * Proceeds only when a session is authenticated, the target's tenant is the
 * session's tenant, and its user is unknown or the session's user. Otherwise:
 * not authenticated (or another user of this tenant) → parked, `'parked'`;
 * authenticated but another company → `'foreign_tenant'` + the
 * `notifications.navigation.otherCompany` toast, nothing parked (a company
 * switch signs out, which clears the lot anyway).
 */
export function navigateToRealtimeTarget(
  target: RouteLocationRaw,
  context: RealtimeTargetContext,
): RealtimeNavigationResult {
  const authenticated = hasAccessToken();
  const tenant = normalizeTenant(context.tenant);
  if (authenticated && tenant !== '' && tenant !== tenantStamp()) {
    // A toast failure (a bare router in a test, a torn-down Ionic overlay)
    // must never surface as a throw or an unhandled rejection from a
    // navigation call.
    try {
      void Promise.resolve(useToast().warning(realtimeNavigationCopy('notifications.navigation.otherCompany'))).catch(
        () => undefined,
      );
    } catch {
      /* the toast is advisory */
    }
    return 'foreign_tenant';
  }
  if (!authenticated || !stampsMatchSession(context)) {
    writePendingAction({
      target: toFullPath(target),
      tenant: normalizeTenant(context.tenant),
      userId: context.userId,
      eventId: context.eventId,
      at: Date.now(),
    });
    return 'parked';
  }
  void router.push(target).catch(() => undefined);
  reportOpened(context.eventId);
  return 'navigated';
}

/* ── Parking from the guard and the deep link ───────────────────────────────── */

/**
 * The router guard's park: the protected `fullPath` it bounced to sign-in,
 * tenant-stamped and with an unknown user. A fresher parked action for the
 * SAME target is kept (a cold-start deep link parks first, WITH its event id,
 * and the guard then bounces that very navigation); anything else is replaced —
 * the latest intent wins.
 */
export function parkRouteForSignIn(fullPath: string): void {
  if (!fullPath || !fullPath.startsWith('/')) return;
  const now = Date.now();
  const existing = readPendingAction();
  if (existing && existing.target === fullPath && !isExpired(existing, now)) return;
  writePendingAction({ target: fullPath, tenant: tenantStamp(), userId: '', eventId: null, at: now });
}

/**
 * Parse the deep link the page was opened with — run in `main.ts` module
 * scope BEFORE `createApp`, i.e. before the router's initial navigation.
 *
 * Two forms: the worker's cold-start fragment `#erpat-rt=<uuid>` on any path
 * (the fragment is stripped with `history.replaceState(history.state, '',
 * pathname + search)`; the event id rides the parked action to the consumer,
 * which records the `opened` receipt), and the inbox item link
 * `/tabs/inbox?id=<digits>`. Either parks `pathname + search` for the
 * consumer: with a token already present the router lands there on its own
 * and the replay is a no-op `replace`; without one the guard bounces to
 * sign-in and keeps this park (same target). Returns what was parked, or
 * `null` when the URL carried no deep link.
 */
export function captureDeepLink(): PendingAction | null {
  if (typeof window === 'undefined') return null;
  const { pathname, search, hash } = window.location;
  const match = COLD_START_RE.exec(hash);
  const eventId = match?.[1] ? match[1].toLowerCase() : null;
  const inboxId = pathname === INBOX_PATH ? new URLSearchParams(search).get('id') ?? '' : '';
  if (!eventId && !DIGITS_RE.test(inboxId)) return null;

  if (eventId) {
    try {
      window.history.replaceState(window.history.state, '', pathname + search);
    } catch {
      /* the fragment stays in the URL; harmless */
    }
  }

  const action: PendingAction = {
    target: pathname + search,
    tenant: tenantStamp(),
    userId: '',
    eventId,
    at: Date.now(),
  };
  writePendingAction(action);
  return action;
}

/* ── The one consumer ───────────────────────────────────────────────────────── */

/**
 * Take the parked target for the session that just became authenticated, or
 * `null`. The lot is emptied either way. Dropped silently when older than
 * `PENDING_ACTION_TTL_MS` or stamped for another tenant / user. When a target
 * is returned and it carries an event id, the `opened` receipt is recorded.
 *
 * Callers: `SignInPage` (both success sites — `router.replace(target ??
 * '/tabs/home')`) and `App.vue`'s `isAuthenticated` watcher on the `restore()`
 * path only.
 */
export function consumePendingRoute(): string | null {
  const action = readPendingAction();
  clearPendingAction();
  if (!action) return null;
  if (isExpired(action, Date.now())) return null;
  if (!stampsMatchSession(action)) return null;
  reportOpened(action.eventId);
  return action.target;
}
